import { Component, Vue, Watch } from "vue-property-decorator";
import { Select, Switch, Input, Button, Spin, Alert, Form } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import TitleListState from "@/modules/title-list";
import { deepClone, fetchLevels, getSkillSelected, isAnyVersionSelected } from "@/util/func";
import TextAnalysisGrid from "../components/text-grid/text-analysis-grid.vue";
import { GridCols } from "../components/text-grid/text-analysis-grid";
import { TextAnalysisService } from "@/services/vcs-report";
import { VersionGroupModule } from "@/modules/versiongroup";
import { isGuid } from "commonui";
import { CommonLocale, WordCountTextAnalysisLocale } from "@/locales/localeid";
import { fmtMsg } from "commonui";
import PageTitle from "@/components/page-title/page-title.vue";
import { VCSLevelModel } from "@/models/title-list-model";
import { CurriculumType } from "@/util/enum";

@Component({
	components: {
		Select,
		SelectOption: Select.Option,
		SwitchEl: Switch,
		Textarea: Input.TextArea,
		TextAnalysisGrid,
		Button,
		Spin,
		Alert,
		Form,
		PageTitle
	}
})
export default class TextAnalysis extends Vue {
	titleListModule = getModule(TitleListState);
	versionGroupModule = VersionGroupModule;
	public readonly allLanguageSkill = -1;
	public readonly lableSpan = 6;
	public readonly itemSpan = 18; // for multiple cols
	public readonly lableSpanTwo = 3; // for single cols
	public readonly itemSpanTwo = 21;

	public locale = WordCountTextAnalysisLocale;
	public format = fmtMsg;
	public skillSelected: number[] = [this.allLanguageSkill];
	public lemmaOn: boolean = true;
	public loading = false;
	public units: VCSLevelModel[] = [];
	public unit = this.titleListModule.levels[0]?.id;
	public wordList = "";
	public defaultSource = {
		previousWords: [],
		currentWords: [],
		futureWords: [],
		nonCurriculumWords: [],
		allWords: []
	};
	public source = deepClone(this.defaultSource);
	public noVersion: boolean = false;
	public commonLocale = CommonLocale;
	public form;

	public baseCols: GridCols[] = [
		{
			bindTo: "word",
			heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColWord)
		},
		{
			bindTo: "occurence",
			heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColOccurences)
		},
		{
			bindTo: "exposureLevel",
			heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColFirstUnit),
			render: (exposureLevel: number): string => {
				return this.titleListModule.levels.find((l) => l.id == exposureLevel)?.displayName as string;
			}
		},
		{
			bindTo: "totalOccurrence",
			heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColTotalOccurence)
		}
	];
	public currentUnitCols: GridCols[] = deepClone(this.baseCols);

	public nonCurriculumCols: GridCols[] = [deepClone(this.baseCols[0]), deepClone(this.baseCols[1])];

	public cumulativeCols: GridCols[] = deepClone(this.baseCols);

	@Watch("versionGroupModule.selectedGsVersion")
	@Watch("versionGroupModule.selectedLsVersion")
	refetch() {
		this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion);
		this.unit = this.units[0].id
		if (!isGuid(this.versionGroupModule.selectedGsVersion)) {
			this.unit = this.titleListModule.levels.filter(
				(f) => f.curriculumType === CurriculumType.LittleSEED
			)[0]?.id;
		} else if (!isGuid(this.versionGroupModule.selectedLsVersion)) {
			this.unit = this.titleListModule.levels.filter((f) => f.curriculumType === CurriculumType.GrapeSEED)[0]?.id;
		}

		if (this.processWordList().length && isAnyVersionSelected()) {
			this.fetchReport();
		}
		if (isAnyVersionSelected()) {
			this.noVersion = false;
		} else {
			this.noVersion = true;
		}
	}

	@Watch("versionGroupModule.disabled", { immediate: true })
	enable() {
		VersionGroupModule.disabled && VersionGroupModule.setDisabled(false);
	}

	created() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}

		this.units = fetchLevels(this.titleListModule.levels, this.versionGroupModule.selectedLsVersion, this.versionGroupModule.selectedGsVersion);
		this.unit = this.units[0].id

		this.form = this.$form.createForm(this, { name: "form" });

		this.currentUnitCols.splice(2, 1);
		this.cumulativeCols.splice(
			1,
			2,
			{
				bindTo: "occurence",
				heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColOccurence)
			},
			{
				bindTo: "exposureLevel",
				heading: fmtMsg(WordCountTextAnalysisLocale.WordCountTextAnalysisLocaleGridColExposureLevel),
				render: (exposureLevel: number): string => {
					return this.titleListModule.levels.find((l) => l.id == exposureLevel)?.displayName as string;
				}
			}
		);
	}

	beforeDestroy() {
		VersionGroupModule.setDisabled(true);
	}

	fetchReport(e?) {
		if (e) {
			e.preventDefault();
		}
		this.loading = true;
		TextAnalysisService.getTextAnalysisReport({
			gSVersionGroupId: isGuid(VersionGroupModule.selectedGsVersion)
				? VersionGroupModule.selectedGsVersion
				: undefined,
			lSVersionGroupId: isGuid(VersionGroupModule.selectedLsVersion)
				? VersionGroupModule.selectedLsVersion
				: undefined,
			isLemmaOn: this.lemmaOn,
			unit: this.titleListModule.levels.find((l) => l.id === this.unit)?.unit as number,
			languageSkillIds: this.processLanguageSkillId(),
			wordString: this.processWordList(),
			unitCurriculumType: this.titleListModule.levels.find((l) => l.id === this.unit)?.curriculumType as number
		})
			.then((data) => {
				this.source = data;
				this.loading = false;
			})
			.catch(() => {
				this.source = deepClone(this.defaultSource);
				this.loading = false;
			});
	}

	processWordList() {
		const special = ":;\"<>?/!`~@#$%^&*()+=-_.{}[]|\\' ".split("");
		const trim = (input: string) => {
			const inTrim = (str: string) => {
				const spStr = str.split("");
				let deleteTill = 0;
				let startChar = spStr[deleteTill];
				while (special.some((s) => s === startChar)) {
					deleteTill++;
					if (deleteTill <= spStr.length) {
						startChar = spStr[deleteTill];
					} else {
						deleteTill--;
						break;
					}
				}
				spStr.splice(0, deleteTill);
				return spStr.join("");
			};
			input = inTrim(input);
			input = inTrim(input.split("").reverse().join("")).split("").reverse().join("");
			return input;
		};
		const list = this.wordList
			.replace(/,/gm, " ")
			.replace(/[\r\n]+|[\r]+|[\n]+|[—]+/g, " ")
			.split(" ")
			.filter((w) => w.trim().length)
			.map((w) => trim(w.trim().toLowerCase()))
			.filter((w) => w.trim().length)
			.join(",");
		return list ? list : "";
	}

	processLanguageSkillId() {
		if (this.skillSelected[0] === -1) {
			return undefined;
		}
		return this.skillSelected;
	}

	selectControlContainer(element: HTMLDivElement) {
		return element.parentNode;
	}

	onSkillChange(selectedValues) {
		this.skillSelected = getSkillSelected(selectedValues);
	}

	onLemmaChange() {
		// refetch data
	}
}
